import React, { useState, useEffect, useRef } from "react";
import {
  Autocomplete,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Typography,
  Switch,
  FormControlLabel,
} from "@mui/material";
import useToast from "../../hooks/useToast";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { saveApps, selectApp } from "../../slices/AppsSlice";
import { getSubdomainAndMainDomain } from "../../utils";
import FileUpload from "../../components/FileUpload/FileUpload";
import { ACCEPTED_LOGO_FORMATS, CLIENT_TYPES, COUNTRIES, LANGUAGES, TIMEZONES } from "../../constants/Constants";
import useApiCall from "../../hooks/useApiCall";
import { INDIGO, SLATE } from "../../constants/Colors";
import { usePageMeta } from "../../contexts/PageMetaContext";
import BottomActionBar from "../../components/BottomActionBar";
import TrimTextField from "../../components/TrimTextField";

const FIELD_LABEL_SX = {
  fontSize: "0.8125rem",
  fontWeight: 500,
  color: SLATE[700],
  mb: "6px",
  display: "block",
};

const INPUT_SX = {
  "& .MuiOutlinedInput-root": {
    borderRadius: "8px",
    fontSize: "0.875rem",
    bgcolor: "#ffffff",
    "& fieldset": { borderColor: SLATE[300] },
    "&:hover fieldset": { borderColor: SLATE[400] },
    "&.Mui-focused fieldset": { borderColor: INDIGO[500] },
  },
  "& .MuiInputBase-input": { color: SLATE[900] },
};

function SectionHeader({ title, description }) {
  return (
    <Box sx={{ mb: 2.5 }}>
      <Typography sx={{ fontSize: "0.9375rem", fontWeight: 600, color: SLATE[900] }}>
        {title}
      </Typography>
      {description && (
        <Typography sx={{ fontSize: "0.8125rem", color: SLATE[500], mt: "2px" }}>
          {description}
        </Typography>
      )}
    </Box>
  );
}

export default function ManageClient(props) {
  const tokens = useSelector((state) => state?.tokens.value);
  const currentApp = useSelector((state) => state?.apps?.selectedApp);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { makeRequest } = useApiCall();
  const { setPageMeta } = usePageMeta();
  const { success } = useToast();
  const [saving, setSaving] = useState(false);
  const [clientMode, setClientMode] = useState(currentApp?.client_mode || (props.edit ? 'legacy' : 'agent'));
  const [pendingMode, setPendingMode] = useState(null);
  const [modeDialogOpen, setModeDialogOpen] = useState(false);
  const [savingMode, setSavingMode] = useState(false);
  const countryInputRef = useRef(null);
  const clientTypeInputRef = useRef(null);
  const urlCodeInputRef = useRef(null);

  const initialState = {
    client_name: "",
    primary_website: "",
    logo_url: "",
    country: "",
    market: "",
    url_code: "",
    copy_market: "",
    client_type: "",
    agent_type: "",
    onboard_existing_client: false,
    trigger_points: [],
    mode_env: "staging",
    insync_prod_app_id: "",
    metadata: {
      primary_color: "#4F46E5",
      language: "English",
      timezone: "Eastern Time (ET)",
      business_context: "",
    },
  };

  const originalDataRef = useRef(props.edit ? currentApp : initialState);
  const [appData, setAppData] = useState(props.edit ? currentApp : initialState);

  useEffect(() => {
    return () => setPageMeta({});
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const isDirty = JSON.stringify(appData) !== JSON.stringify(originalDataRef.current);

  const [retriggerDialogOpen, setRetriggerDialogOpen] = useState(false);
  const [retriggering, setRetriggering] = useState(false);
  const [genRetriggered, setGenRetriggered] = useState(false);
  const generationFailed =
    props.edit && currentApp?.metadata?.generation?.status === "failure" && !genRetriggered;

  useEffect(() => {
    if (!props.edit) return;
    setPageMeta(prev => ({ ...prev, badge: clientMode }));
  }, [clientMode, props.edit]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleModeChange = (_e, newMode) => {
    if (!newMode || newMode === clientMode) return;
    if (!props.edit) {
      setClientMode(newMode);
      return;
    }
    if (['agent', 'hybrid'].includes(clientMode)) {
      setPendingMode(newMode);
      setModeDialogOpen(true);
    } else {
      applyModeChange(newMode);
    }
  };

  const applyModeChange = async (mode) => {
    setSavingMode(true);
    try {
      await makeRequest(`/v1/apps/${currentApp?.id}`, {
        method: 'PUT',
        body: { client_mode: mode },
        authTokens: tokens,
      });
      setClientMode(mode);
      success('Client mode updated.');
    } catch {
      // toasted by useApiCall
    } finally {
      setSavingMode(false);
      setModeDialogOpen(false);
      setPendingMode(null);
    }
  };

  const handleRetrigger = async () => {
    setRetriggering(true);
    try {
      await makeRequest(`/v1/apps/${currentApp?.id}/retrigger_generation`, {
        method: "POST",
        authTokens: tokens,
      });
      setGenRetriggered(true);
      success("Market generation re-triggered.");
    } catch {
      // toasted by useApiCall
    } finally {
      setRetriggering(false);
      setRetriggerDialogOpen(false);
    }
  };

  const handleChange = (e, key) => {
    let value = key === "logo_url" ? e.target.value[0] : e.target.value;
    let newObj = { ...appData, [key]: value };
    if (key === "primary_website") {
      newObj.url_code = getSubdomainAndMainDomain(value);
      if (urlCodeInputRef.current) urlCodeInputRef.current.setCustomValidity("");
    }
    setAppData((prev) => ({ ...prev, ...newObj }));
  };

  const handleLogoChange = (e) => {
    handleChange(e, "logo_url");
  };

  const handleMetaChange = (key, value) => {
    setAppData((prev) => ({ ...prev, metadata: { ...prev.metadata, [key]: value } }));
  };

  const handleSave = async () => {
    setSaving(true);
    const requestData = {
      client_name: appData.client_name,
      primary_website: appData.primary_website,
      url_code: appData.url_code,
      market: `${appData.country?.toLowerCase()}_${appData.url_code}`,
      logo_url: appData.logo_url,
      country: appData.country,
      copy_market: appData.copy_market,
      client_type: appData.client_type,
      agent_type: appData.agent_type,
      onboard_existing_client: appData.onboard_existing_client,
      trigger_points: appData.trigger_points,
      insync_prod_app_id: appData.insync_prod_app_id,
      metadata: appData.metadata,
      mode_env: appData.mode_env || "staging",
      ...(!props.edit && { client_mode: clientMode }),
    };

    try {
      const response = props.edit
        ? await makeRequest(`/v1/apps/${currentApp?.id}`, { method: "PUT", body: requestData, authTokens: tokens })
        : await makeRequest("/v1/apps", { method: "POST", body: requestData, authTokens: tokens });

      if (response) {
        if (!props.edit) {
          dispatch(selectApp(response));
          navigate(`/${response.url_code}/`);
        } else {
          dispatch(selectApp({ ...currentApp, ...appData }));
          navigate("/");
        }
      }
    } catch (_err) {
      // error already toasted by useApiCall
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box component="form" onSubmit={(e) => { e.preventDefault(); handleSave(); }} sx={{ width: "100%", maxWidth: 720, pb: 10 }}>
      {/* Basic Info */}
      <Box
        sx={{
          bgcolor: "#ffffff",
          border: `1px solid ${SLATE[200]}`,
          borderRadius: "16px",
          p: 3,
          mb: 2.5,
        }}
      >
        <SectionHeader
          title="Basic Information"
          description="The core details used to identify this client."
        />

        <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 2.5 }}>
          {/* Client Name */}
          <Box sx={{ gridColumn: "1 / -1" }}>
            <Typography component="label" sx={FIELD_LABEL_SX}>Client Name <Box component="span" sx={{ color: "#ef4444" }}>*</Box></Typography>
            <TrimTextField
              fullWidth
              size="small"
              required
              placeholder="e.g. AllCampus"
              value={appData.client_name || ""}
              onChange={(e) => handleChange(e, "client_name")}
              sx={INPUT_SX}
              inputProps={{
                onInvalid: (e) => e.target.setCustomValidity("Please enter a client name"),
                onInput: (e) => e.target.setCustomValidity(""),
              }}
            />
          </Box>

          {/* Client Type */}
          {!props.edit && (
            <Box>
              <Typography component="label" sx={FIELD_LABEL_SX}>Client Type <Box component="span" sx={{ color: "#ef4444" }}>*</Box></Typography>
              <Autocomplete
                size="small"
                options={CLIENT_TYPES}
                value={CLIENT_TYPES.find((ct) => ct.agent_type === appData.agent_type) || null}
                getOptionLabel={(option) => option.title || option}
                onChange={(_e, val) => {
                  handleChange({ target: { value: val?.value || "" } }, "copy_market");
                  handleChange({ target: { value: val?.agent_type || "" } }, "agent_type");
                  handleChange({ target: { value: val?.client_type || "" } }, "client_type");
                  if (clientTypeInputRef.current) clientTypeInputRef.current.setCustomValidity("");
                }}
                renderInput={(params) => (
                  <TextField {...params} required placeholder="Select type..." sx={INPUT_SX}
                    inputRef={clientTypeInputRef}
                    inputProps={{ ...params.inputProps, onInvalid: (e) => e.target.setCustomValidity("Please select a client type") }}
                  />
                )}
              />
            </Box>
          )}

          {/* Country */}
          <Box>
            <Typography component="label" sx={FIELD_LABEL_SX}>Country / Region <Box component="span" sx={{ color: "#ef4444" }}>*</Box></Typography>
            <Autocomplete
              size="small"
              options={COUNTRIES}
              disabled={props.edit}
              inputValue={appData.country || ""}
              onChange={(_e, val) => {
                handleChange({ target: { value: val?.value || "" } }, "country");
                if (countryInputRef.current) countryInputRef.current.setCustomValidity("");
              }}
              renderInput={(params) => (
                <TextField {...params} required placeholder="Select country..." sx={INPUT_SX}
                  inputRef={countryInputRef}
                  inputProps={{ ...params.inputProps, onInvalid: (e) => e.target.setCustomValidity("Please select a country") }}
                />
              )}
            />
          </Box>

          {/* Primary Website */}
          <Box sx={{ gridColumn: "1 / -1" }}>
            <Typography component="label" sx={FIELD_LABEL_SX}>Website URL <Box component="span" sx={{ color: "#ef4444" }}>*</Box></Typography>
            <TrimTextField
              fullWidth
              size="small"
              required
              placeholder="https://example.com"
              value={appData.primary_website || ""}
              onChange={(e) => handleChange(e, "primary_website")}
              sx={INPUT_SX}
              inputProps={{
                onInvalid: (e) => e.target.setCustomValidity("Please enter a website URL"),
                onInput: (e) => e.target.setCustomValidity(""),
              }}
            />
          </Box>

          {/* Client Code */}
          <Box>
            <Typography component="label" sx={FIELD_LABEL_SX}>
              Client Code <Box component="span" sx={{ color: "#ef4444" }}>*</Box>
              <Typography component="span" sx={{ fontSize: "0.75rem", color: SLATE[400], ml: 1 }}>
                (auto-generated)
              </Typography>
            </Typography>
            <TextField
              fullWidth
              size="small"
              required
              disabled={props.edit}
              placeholder="e.g. allcampus"
              value={appData.url_code || ""}
              onChange={(e) => handleChange(e, "url_code")}
              sx={INPUT_SX}
              inputRef={urlCodeInputRef}
              inputProps={{
                onInvalid: (e) => e.target.setCustomValidity("Please enter a client code"),
                onInput: (e) => e.target.setCustomValidity(""),
              }}
            />
          </Box>

          {/* Primary Color */}
          <Box>
            <Typography component="label" sx={FIELD_LABEL_SX}>Primary Color</Typography>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, overflow: "hidden" }}>
              <Box
                component="input"
                type="color"
                value={appData.metadata?.primary_color || "#4F46E5"}
                onChange={(e) => handleMetaChange("primary_color", e.target.value)}
                sx={{
                  width: 40,
                  height: 36,
                  border: `1px solid ${SLATE[300]}`,
                  borderRadius: "8px",
                  cursor: "pointer",
                  padding: "2px",
                  bgcolor: "#ffffff",
                }}
              />
              <TextField
                size="small"
                value={appData.metadata?.primary_color || "#4F46E5"}
                onChange={(e) => handleMetaChange("primary_color", e.target.value)}
                placeholder="#4F46E5"
                sx={{ ...INPUT_SX, flex: 1 }}
              />
            </Box>
          </Box>

          {/* Default Language */}
          <Box>
            <Typography component="label" sx={FIELD_LABEL_SX}>Default Language</Typography>
            <Autocomplete
              size="small"
              options={LANGUAGES}
              value={LANGUAGES.find((l) => l.value === appData.metadata?.language) || LANGUAGES[0]}
              getOptionLabel={(option) => option.label}
              disableClearable
              onChange={(_e, val) => handleMetaChange("language", val?.value || "English")}
              renderInput={(params) => (
                <TextField {...params} sx={INPUT_SX} />
              )}
            />
          </Box>

          {/* Default Timezone */}
          <Box>
            <Typography component="label" sx={FIELD_LABEL_SX}>Default Timezone</Typography>
            <Autocomplete
              size="small"
              options={TIMEZONES}
              value={TIMEZONES.find((t) => t.value === appData.metadata?.timezone) || TIMEZONES[3]}
              getOptionLabel={(option) => option.label}
              disableClearable
              onChange={(_e, val) => handleMetaChange("timezone", val?.value || "Eastern Time (ET)")}
              renderInput={(params) => (
                <TextField {...params} sx={INPUT_SX} />
              )}
            />
          </Box>

        </Box>
      </Box>

      {/* Client Mode — create only */}
      {!props.edit && (() => {
        const MODES = [
          { value: 'legacy', label: 'Legacy',   color: '#64748b', dot: '#94a3b8' },
          { value: 'hybrid', label: 'Hybrid',   color: '#2563eb', dot: '#60a5fa' },
          { value: 'agent',  label: 'AI Agent', color: '#16a34a', dot: '#4ade80' },
        ];
        const modeIndex = MODES.findIndex(m => m.value === clientMode);
        const activeMode = MODES[modeIndex];
        return (
          <Box sx={{ bgcolor: '#ffffff', border: `1px solid ${SLATE[200]}`, borderRadius: '16px', p: 3, mb: 2.5 }}>
            <SectionHeader
              title="Client Mode"
              description="Choose the mode for this new client."
            />
            <Box
              sx={{
                position: 'relative',
                display: 'flex',
                bgcolor: SLATE[100],
                borderRadius: '12px',
                p: '4px',
                opacity: 1,
                userSelect: 'none',
                maxWidth: 360,
              }}
            >
              {/* Sliding pill */}
              <Box
                sx={{
                  position: 'absolute',
                  top: '4px',
                  bottom: '4px',
                  width: 'calc((100% - 8px) / 3)',
                  left: `calc(${modeIndex} * (100% - 8px) / 3 + 4px)`,
                  borderRadius: '8px',
                  bgcolor: '#fff',
                  boxShadow: '0 1px 4px rgba(0,0,0,0.13)',
                  transition: 'left 0.22s cubic-bezier(0.4,0,0.2,1)',
                }}
              />
              {MODES.map(({ value, label, color }) => {
                const active = clientMode === value;
                return (
                  <Box
                    key={value}
                    onClick={() => handleModeChange(null, value)}
                    sx={{
                      flex: 1,
                      textAlign: 'center',
                      py: '9px',
                      borderRadius: '8px',
                      cursor: 'pointer',
                      position: 'relative',
                      zIndex: 1,
                      transition: 'color 0.2s',
                    }}
                  >
                    <Typography
                      sx={{
                        fontSize: '0.8125rem',
                        fontWeight: active ? 700 : 500,
                        color: active ? color : SLATE[500],
                        lineHeight: 1,
                        transition: 'color 0.2s, font-weight 0.1s',
                      }}
                    >
                      {label}
                    </Typography>
                  </Box>
                );
              })}
            </Box>
            {activeMode && (
              <Typography sx={{ mt: 1.5, fontSize: '0.775rem', color: SLATE[500] }}>
                {clientMode === 'legacy' && 'Standard configuration — no agentic AI features.'}
                {clientMode === 'hybrid' && 'Mix of legacy and agentic features.'}
                {clientMode === 'agent' && 'Full agentic AI — enables AI agent builder and chat.'}
              </Typography>
            )}
          </Box>
        );
      })()}

      {/* Logo & Branding */}
      <Box
        sx={{
          bgcolor: "#ffffff",
          border: `1px solid ${SLATE[200]}`,
          borderRadius: "16px",
          p: 3,
          mb: 2.5,
        }}
      >
        <SectionHeader
          title="Logo & Branding"
          description="Upload a client logo to display on cards and the sidebar."
        />
        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
          <Box
            sx={{
              width: 64,
              height: 64,
              borderRadius: "12px",
              border: `1px solid ${SLATE[200]}`,
              bgcolor: SLATE[50],
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              overflow: "hidden",
              flexShrink: 0,
            }}
          >
            {appData.logo_url ? (
              <img src={encodeURI(appData.logo_url)} alt="logo" style={{ width: 48, height: 48, objectFit: "contain" }} />
            ) : (
              <Typography sx={{ fontSize: "0.6875rem", color: SLATE[400] }}>No logo</Typography>
            )}
          </Box>
          <Box sx={{ flex: 1 }}>
            <TrimTextField
              fullWidth
              size="small"
              placeholder="https://logo.clearbit.com/example.com"
              value={appData.logo_url || ""}
              onChange={(e) => handleChange(e, "logo_url")}
              sx={{ ...INPUT_SX, mb: 1 }}
            />
            <FileUpload
              field={{ name: "fileUpload", title: appData.logo_url ? "Change Logo" : "Upload Logo", multiple: false }}
              accept={ACCEPTED_LOGO_FORMATS}
              endpoint="/v1/apps/logo_upload"
              onChange={handleLogoChange}
            />
          </Box>
        </Box>
      </Box>

      {/* Additional Details */}
      <Box
        sx={{
          bgcolor: "#ffffff",
          border: `1px solid ${SLATE[200]}`,
          borderRadius: "16px",
          p: 3,
          mb: 2.5,
        }}
      >
        <SectionHeader
          title="Additional Details"
          description="Optional fields for internal reference."
        />
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
          <Box>
            <Typography component="label" sx={FIELD_LABEL_SX}>PROD App ID</Typography>
            <TrimTextField
              fullWidth
              size="small"
              placeholder="Production App ID"
              value={appData.insync_prod_app_id || ""}
              onChange={(e) => handleChange(e, "insync_prod_app_id")}
              sx={INPUT_SX}
            />
          </Box>
          <Box>
            <Typography component="label" sx={FIELD_LABEL_SX}>Business Context</Typography>
            <TrimTextField
              fullWidth
              size="small"
              multiline
              minRows={3}
              placeholder="Describe the client's business, goals, and any context relevant to the AI agent..."
              value={appData.metadata?.business_context || ""}
              onChange={(e) => handleMetaChange("business_context", e.target.value)}
              sx={INPUT_SX}
            />
          </Box>
          {!props.edit && (
            <FormControlLabel
              control={
                <Switch
                  checked={appData.onboard_existing_client || false}
                  onChange={(e) =>
                    setAppData((prev) => ({ ...prev, onboard_existing_client: e.target.checked }))
                  }
                  sx={{
                    "& .MuiSwitch-switchBase.Mui-checked": { color: INDIGO[600] },
                    "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": { bgcolor: INDIGO[400] },
                  }}
                />
              }
              label={
                <Typography sx={{ fontSize: "0.875rem", color: SLATE[700] }}>
                  Onboard Existing Client
                </Typography>
              }
            />
          )}
        </Box>
      </Box>

      {generationFailed && (
        <Box sx={{ mt: 2 }}>
          <Typography sx={{ fontSize: "0.875rem", color: "#C62828", mb: 1 }}>
            The last market-generation run failed
            {currentApp?.metadata?.generation?.build_number
              ? ` (build #${currentApp.metadata.generation.build_number})`
              : ""}
            .
          </Typography>
          <Button
            onClick={() => setRetriggerDialogOpen(true)}
            variant="outlined"
            color="error"
            size="small"
            sx={{ textTransform: "none" }}
          >
            Re-trigger market generation
          </Button>
        </Box>
      )}

      <BottomActionBar
        name={props.edit ? "Save Client" : "Create Client"}
        handleCancel={() => navigate("/")}
        saving={saving}
        isDirty={isDirty}
      />

      <Dialog open={modeDialogOpen} onClose={() => { setModeDialogOpen(false); setPendingMode(null); }} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontSize: '1rem', fontWeight: 600, color: SLATE[900], pb: 1 }}>
          Change Client Mode?
        </DialogTitle>
        <DialogContent sx={{ pt: '8px !important' }}>
          <Typography sx={{ fontSize: '0.875rem', color: SLATE[600] }}>
            Changing mode will hide agent configuration sections. This does not delete any agent data.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5, pt: 1.5, gap: 1, borderTop: `1px solid ${SLATE[100]}` }}>
          <Button onClick={() => { setModeDialogOpen(false); setPendingMode(null); }} variant="outlined" size="small" sx={{ textTransform: 'none', borderColor: SLATE[300], color: SLATE[700] }}>
            Cancel
          </Button>
          <Button onClick={() => applyModeChange(pendingMode)} disabled={savingMode} variant="contained" size="small" sx={{ textTransform: 'none', boxShadow: 'none', '&:hover': { boxShadow: 'none' } }}>
            {savingMode ? 'Saving...' : 'Confirm'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={retriggerDialogOpen} onClose={() => setRetriggerDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontSize: '1rem', fontWeight: 600, color: SLATE[900], pb: 1 }}>
          Re-trigger market generation?
        </DialogTitle>
        <DialogContent sx={{ pt: '8px !important' }}>
          <Typography sx={{ fontSize: '0.875rem', color: SLATE[600] }}>
            This re-runs the file-generation job for {currentApp?.market}. The job resets its
            own working branches before regenerating, so it is safe to run again.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5, pt: 1.5, gap: 1, borderTop: `1px solid ${SLATE[100]}` }}>
          <Button onClick={() => setRetriggerDialogOpen(false)} variant="outlined" size="small" sx={{ textTransform: 'none', borderColor: SLATE[300], color: SLATE[700] }}>
            Cancel
          </Button>
          <Button onClick={handleRetrigger} disabled={retriggering} variant="contained" color="error" size="small" sx={{ textTransform: 'none', boxShadow: 'none', '&:hover': { boxShadow: 'none' } }}>
            {retriggering ? 'Triggering...' : 'Re-trigger'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
